// ---------------------------------------------------------------------------
// The open project's own URL, `/p/<projectId>` — the same id string already
// used as the IndexedDB record id (see storage.ts), stable across renames.
// Lets refreshing, bookmarking, or sharing the URL reopen the exact same
// project instead of always landing on Landing.tsx. Pure, testable
// functions in the same style as featureFlags.ts (window.location-defaulted
// params, no class/singleton) — the actual history.pushState/replaceState
// calls and the state<->URL wiring live in useProjectPersistence.ts, which
// is the one place that needs to reason about WHEN to write, not just how
// to build the URL string.
// ---------------------------------------------------------------------------

const PROJECT_PATH_PREFIX = "/p/";

/**
 * "/p/<id>" -> "<id>"; null if the path doesn't match — includes "/p/" and
 * "/p" (no id at all) and "/p/<id>/extra" (only an exact single segment
 * counts, so a stray trailing path never gets truncated into a wrong id).
 */
export function projectIdFromLocation(pathname: string = window.location.pathname): string | null {
  const segments = pathname.split("/").filter((s) => s.length > 0);
  if (segments.length !== 2 || segments[0] !== "p") return null;
  return segments[1];
}

/** "<id>" -> "/p/<id>" */
export function projectPath(id: string): string {
  return PROJECT_PATH_PREFIX + id;
}

/**
 * The full URL to push/replace into history for a given project id (or
 * `null` for "no project open" -> "/") — preserves whatever `?search`/
 * `#hash` is already on `currentHref` (e.g. `?calibrator`, see
 * featureFlags.ts), only overwriting the pathname, so this can never
 * accidentally drop or reorder an existing query string.
 */
export function projectUrl(id: string | null, currentHref: string = window.location.href): string {
  const url = new URL(currentHref);
  url.pathname = id === null ? "/" : projectPath(id);
  return url.toString();
}
